// app/customs/spreadcontrol/index.tsx
'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import axios from 'axios';
import * as Global from '@utils/global';
import * as AuthContext from '@/utils/authcontext';
import SpreadTable from '@/customs/spreadtable';
import ErrorMessage from '@/customs/errormessage';
import './index.css';

type SpreadMenuItem = {
  spread_menu_guid: string;
  spread_id: string;
  title: string;
  permission: string;
  base_filters?: Record<string, string>;
  ui_filters?: Record<string, string>;
  form_pkey: string;
  form_cursor_type?: string | Global.FormCursorType;  
  output_type?: string | Global.OutputType;  
};

type SpreadMenuGroup = {
  groupid: string;
  group_title: string;
  items: SpreadMenuItem[];
};

const SpreadControl: React.FC = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const spreadMenuGuid = searchParams.get('spread_menu_guid');

  const [menuGroups, setMenuGroups] = useState<SpreadMenuGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const { authToken, employeeDomain } = AuthContext.useAuth();
  // ② コンポーネント内の state にこれを追加（共通エラー状態を保持）
  const [errorMessage, setErrorMessage] = useState<string | null>(null);


  useEffect(() => {
    if (!authToken || !employeeDomain) {
      setMenuGroups([]);
      setLoading(false);
      return;
    }

    const fetchMenu = async () => {
      setLoading(true);
      try {
        // const res = await axios.post<SpreadMenuGroup[]>(
        //   `${Global.getApiBaseUrl()}/spread/get_spreadmenu`,
        //   { 社員GUID: employeeGUID }
        // );

        const res = await axios.post<SpreadMenuGroup[]>(
          `${Global.getApiBaseUrl()}/spread/get_spreadmenu`,
          {
            base_token_interface: Global.createBaseTokenInterface(authToken, employeeDomain)
          }
        );

        setMenuGroups(res.data);
      } catch (err) {
        setErrorMessage(`メニュー取得エラー||${(err as Error)?.message || JSON.stringify(err)}`);
        console.error("メニュー取得エラー", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [employeeDomain]);

  if (loading) return <div>読み込み中...</div>;

  // spread_menu_guid 優先で選択
  if (spreadMenuGuid) {
    let selected: SpreadMenuItem | undefined;
    for (const g of menuGroups) {
      selected = g.items.find(i => i.spread_menu_guid === spreadMenuGuid);
      if (selected) break;
    }

    if (!selected) return <div>この帳票にはアクセスできません</div>;

    return (
      <SpreadTable
        spread={selected.spread_id}
        base_filters={selected.base_filters}
        ui_filters={selected.ui_filters}
        form_pkey={selected.form_pkey}
        form_cursor_type={selected.form_cursor_type}
        output_type={selected.output_type}
      />
    );
  }

  return (
    <div className='spread-control-container'>
      {/* エラーメッセージ表示 */}
      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
          type="error"
        />
      )}
      {/* <h2>帳票メニュー</h2> */}
      {menuGroups.map(group => (
        <div key={group.groupid} className="spreadmenu-group">
          <div className="spread-grouptitle">
            <i className="fa-solid fa-layer-group" style={{marginRight:4}} />
            {group.group_title}
          </div>
          <div className="spreadmenu-container">
            {group.items.map((item) => (
              <button
                key={item.spread_menu_guid}
                className="spreadmenu-button"
                onClick={() => {
                  router.push(`?spread_menu_guid=${item.spread_menu_guid}`);
                }}
              >
                <div className="spreadmenu-title">{item.title}</div>
                <div className="spreadmenu-meta">
                  {item.spread_id}：{item.permission}
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SpreadControl;
